import { useSuspenseQuery } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { Suspense } from "react";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { FinanceiroLabels } from "@/lib/labels/financeiro";

export function ReceivablesPanel({ month }: { month: string }) {
	return (
		<ErrorBoundary key={month}>
			<Suspense fallback={<Skeleton className="mt-5 h-44 w-full rounded-lg" />}>
				<ReceivablesContent month={month} />
			</Suspense>
		</ErrorBoundary>
	);
}

function ReceivablesContent({ month }: { month: string }) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.receivables.forecast.queryOptions({ input: { month } }),
	);
	const forecasts = data.data.filter((forecast) => forecast.total_cents !== 0);

	return (
		<section className="surface-panel mt-5">
			<header className="flex items-end justify-between gap-4 p-4">
				<div>
					<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						Rede a receber
					</p>
					<p className="mt-2 font-mono text-xl font-semibold text-foreground sm:text-2xl">
						{FinanceiroLabels.money(data.total_cents)}
					</p>
				</div>
				<CreditCard className="size-5 text-primary" />
			</header>

			{forecasts.length === 0 && (
				<div className="border-t border-border">
					<EmptyState
						icon={CreditCard}
						title="Nenhum repasse previsto"
						description="As vendas no cartão ainda a receber aparecem aqui."
					/>
				</div>
			)}

			{forecasts.length > 0 && (
				<ul className="divide-y divide-border border-t border-border">
					{forecasts.map((forecast) => (
						<li
							key={forecast.month}
							className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center"
						>
							<div>
								<p className="text-sm font-semibold capitalize text-foreground">
									{FinanceiroLabels.monthLongYear(forecast.month)}
								</p>
								<p className="mt-0.5 font-mono text-xs text-muted-foreground">
									{FinanceiroLabels.money(forecast.total_cents)}
								</p>
							</div>
							<div className="sm:text-right">
								<p className="text-xs text-muted-foreground">Confirmado pela Rede</p>
								<p className="font-mono text-xs font-semibold text-foreground">
									{FinanceiroLabels.money(forecast.exact_cents)}
								</p>
							</div>
							<div className="sm:text-right">
								<p className="text-xs text-muted-foreground">Estimado</p>
								<p className="font-mono text-xs font-semibold text-warning">
									{FinanceiroLabels.money(forecast.estimated_cents)}
								</p>
							</div>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
